import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { campaignAPI, authAPI } from '../../services/api';
import { useAuth } from '../../contexts/AuthContext';
import FileUploadProgress from '../Common/FileUploadProgress';
import {
    CheckIcon,
    XMarkIcon,
    ChartBarIcon,
    DocumentArrowUpIcon,
    InformationCircleIcon,
    CreditCardIcon,
    ExclamationTriangleIcon,
    PencilSquareIcon
} from '@heroicons/react/24/outline';

const EditCampaign = () => {
    const { id } = useParams();
    const navigate = useNavigate();
    const { user, updateUser } = useAuth();
    const [loading, setLoading] = useState(false);
    const [campaign, setCampaign] = useState(null);
    const [csvFile, setCsvFile] = useState(null);
    const [useEnhancedUpdate, setUseEnhancedUpdate] = useState(false);
    const [userCredits, setUserCredits] = useState(null);
    const [uploadProgress, setUploadProgress] = useState(0);
    const [uploadStage, setUploadStage] = useState('uploading');
    const [uploadSpeed, setUploadSpeed] = useState(0);
    const [processedRecords, setProcessedRecords] = useState(0);
    const [totalRecords, setTotalRecords] = useState(0);
    const [validRecords, setValidRecords] = useState(0);
    const [invalidRecords, setInvalidRecords] = useState(0);
    const [duplicateRecords, setDuplicateRecords] = useState(0);
    const [contactsUpdated, setContactsUpdated] = useState(0);
    const [uploadError, setUploadError] = useState(null);
    // New state for database write progress
    const [recordsWritten, setRecordsWritten] = useState(0);
    const [recordsPending, setRecordsPending] = useState(0);
    const [writeProgress, setWriteProgress] = useState(0);
    const [isUploading, setIsUploading] = useState(false);
    const [formData, setFormData] = useState({
        totalNumbers: 0,
        count: 1,
        sent: 0,
        delivered: 0,
        failed: 0,
        pending: 0,
        pulseDuration: 1,
        heard100Percent: 0,
        heard80to99Percent: 0,
        heard50to79Percent: 0,
        heard30to49Percent: 0,
        heard1to29Percent: 0,
        status: 'Ongoing'
    });

    useEffect(() => {
        fetchCampaign();
    }, [id]);

    // Fetch user credits after campaign data is loaded
    useEffect(() => {
        if (campaign) {
            fetchUserCredits();
        }
    }, [campaign]);

    const fetchUserCredits = async () => {
        try {
            // If we have campaign data, fetch the campaign creator's credits
            if (campaign && campaign.createdBy) {
                // We need to get the campaign creator's credits, not the current logged-in user's credits
                const response = await campaignAPI.getCampaignById(id);
                const campaignData = response.data.data.campaign;

                // The campaign should include populated createdBy user data with credits
                if (campaignData.createdBy && typeof campaignData.createdBy === 'object') {
                    setUserCredits(campaignData.createdBy);
                } else {
                    // Fallback: fetch current user credits (for backward compatibility)
                    const userResponse = await authAPI.getProfile();
                    if (userResponse.data.success) {
                        setUserCredits(userResponse.data.data.user);
                    }
                }
            }
        } catch (error) {
            console.error('Error fetching user credits:', error);
            // Fallback: fetch current user credits
            try {
                const response = await authAPI.getProfile();
                if (response.data.success) {
                    setUserCredits(response.data.data.user);
                }
            } catch (fallbackError) {
                console.error('Error fetching fallback user credits:', fallbackError);
            }
        }
    };

    const getCampaignCredits = () => {
        if (!userCredits || !campaign) return { available: 0, rate: 1 };

        switch (campaign.type) {
            case 'Voice Call':
                return {
                    available: userCredits.voiceCallCredits,
                    rate: userCredits.creditRates?.voiceCallRate || 1,
                    type: 'Voice Call'
                };
            case 'SMS':
                return {
                    available: userCredits.smsCredits,
                    rate: userCredits.creditRates?.smsRate || 1,
                    type: 'SMS'
                };
            case 'Whatsapp':
                return {
                    available: userCredits.whatsappCredits,
                    rate: userCredits.creditRates?.whatsappRate || 1,
                    type: 'WhatsApp'
                };
            default:
                return { available: 0, rate: 1, type: 'Unknown' };
        }
    };

    const calculateCreditsNeeded = () => {
        if (!campaign) return 0;

        const currentTotal = campaign.statistics?.totalNumbers || 0;
        const newTotal = formData.totalNumbers;

        if (newTotal > currentTotal) {
            const additionalNumbers = newTotal - currentTotal;
            const { rate } = getCampaignCredits();
            return additionalNumbers * rate;
        }

        return 0;
    };

    const calculateCreditsRefund = () => {
        if (!campaign) return 0;

        const currentTotal = campaign.statistics?.totalNumbers || 0;
        const newTotal = formData.totalNumbers;

        if (newTotal < currentTotal) {
            const removedNumbers = currentTotal - newTotal;
            const { rate } = getCampaignCredits();
            return removedNumbers * rate;
        }

        return 0;
    };

    const getMaxAllowedNumbers = () => {
        const { available, rate } = getCampaignCredits();
        const currentTotal = campaign?.statistics?.totalNumbers || 0;
        const maxFromCredits = Math.floor(available / rate) + currentTotal;
        const absoluteMax = 1000000; // 1 million limit

        return Math.min(maxFromCredits, absoluteMax);
    };

    const fetchCampaign = async () => {
        try {
            setLoading(true);
            const response = await campaignAPI.getCampaignById(id);
            const campaignData = response.data.data.campaign;
            setCampaign(campaignData);

            // Get the actual contact data from overallStats for current status counts
            const actualStats = campaignData.overallStats || {
                totalContacts: 0,
                sent: 0,
                delivered: 0,
                failed: 0,
                pending: 0
            };

            // Use campaign.statistics.totalNumbers (the official total) instead of actualStats.totalContacts
            // This ensures the form shows the correct total numbers after updates
            const campaignTotal = campaignData.statistics?.totalNumbers || actualStats.totalContacts || 0;

            setFormData({
                totalNumbers: campaignTotal,
                count: campaignData.statistics?.count || campaignData.chargePerNumber || 1,
                sent: actualStats.sent,
                delivered: actualStats.delivered,
                failed: actualStats.failed,
                pending: actualStats.pending,
                pulseDuration: campaignData.statistics?.pulseDuration || 1,
                heard100Percent: campaignData.listeningStats?.heard100Percent || 0,
                heard80to99Percent: campaignData.listeningStats?.heard80to99Percent || 0,
                heard50to79Percent: campaignData.listeningStats?.heard50to79Percent || 0,
                heard30to49Percent: campaignData.listeningStats?.heard30to49Percent || 0,
                heard1to29Percent: campaignData.listeningStats?.heard1to29Percent || 0,
                status: campaignData.status || 'Ongoing'
            });
        } catch (error) {
            toast.error('Failed to load campaign');
            navigate('/campaigns');
        } finally {
            setLoading(false);
        }
    };

    const handleInputChange = (e) => {
        const { name, value } = e.target;

        if (name === 'totalNumbers') {
            const newValue = parseInt(value) || 0;
            const maxAllowed = getMaxAllowedNumbers();

            if (newValue > maxAllowed) {
                toast.warning(`Maximum allowed numbers: ${maxAllowed.toLocaleString()} (limited by credits or 1M limit)`);
                return;
            }
        }

        setFormData(prev => ({
            ...prev,
            [name]: name === 'status' ? value : name.includes('Percent') ? parseFloat(value) || 0 : parseInt(value) || 0
        }));
    };

    const validateCSVFile = async (file) => {
        return new Promise((resolve, reject) => {
            const reader = new FileReader();
            reader.onload = (e) => {
                try {
                    const text = e.target.result;
                    const lines = text.split('\n').filter(line => line.trim());

                    if (lines.length === 0) {
                        reject(new Error('File is empty'));
                        return;
                    }

                    if (lines.length < 2) {
                        reject(new Error('CSV file must have a header row and at least one data row'));
                        return;
                    }

                    // Check record count limit (500,000 records excluding header)
                    const recordCount = lines.length - 1; // Exclude header row
                    if (recordCount > 500000) {
                        reject(new Error(`CSV file contains ${recordCount.toLocaleString()} records. Maximum allowed is 500,000 records per upload.`));
                        return;
                    }

                    resolve({
                        totalRecords: recordCount,
                        isValid: true
                    });
                } catch (error) {
                    reject(new Error('Failed to parse CSV file: ' + error.message));
                }
            };
            reader.onerror = () => reject(new Error('Failed to read file'));
            reader.readAsText(file);
        });
    };

    const handleFileChange = async (e) => {
        const file = e.target.files[0];
        if (file) {
            const allowedTypes = ['.csv'];
            const fileExtension = '.' + file.name.split('.').pop().toLowerCase();

            if (!allowedTypes.includes(fileExtension)) {
                toast.error('Please upload only CSV files');
                setCsvFile(null);
                e.target.value = '';
                return;
            }

            // Validate CSV content for record count limit
            if (fileExtension === '.csv') {
                try {
                    await validateCSVFile(file);
                    toast.success(`CSV file validated successfully with ${file.size > 1024 * 1024 ? `${(file.size / (1024 * 1024)).toFixed(1)}MB` : `${(file.size / 1024).toFixed(1)}KB`}`);
                } catch (error) {
                    toast.error(error.message);
                    setCsvFile(null);
                    e.target.value = '';
                    return;
                }
            }

            setCsvFile(file);
            setUseEnhancedUpdate(true);
        } else {
            setCsvFile(null);
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        // Validate campaign statistics
        const totalNumbers = formData.totalNumbers;
        const sent = formData.sent;
        const delivered = formData.delivered;
        const failed = formData.failed;
        const pending = formData.pending;

        // Check maximum limit
        if (totalNumbers > 1000000) {
            toast.error('Maximum allowed total numbers is 1,000,000');
            return;
        }

        // Check credits before processing
        const creditsNeeded = calculateCreditsNeeded();
        const { available } = getCampaignCredits();

        if (creditsNeeded > available) {
            toast.error(`Insufficient credits! Need ${creditsNeeded} credits but only have ${available} available.`);
            return;
        }

        // Check if individual counts are negative
        if (sent < 0 || delivered < 0 || failed < 0 || pending < 0) {
            toast.error('All status counts cannot be negative');
            return;
        }

        // Check if individual counts exceed total numbers
        if (sent > totalNumbers) {
            toast.error(`Sent count (${sent}) cannot exceed total numbers (${totalNumbers})`);
            return;
        }

        if (delivered > totalNumbers) {
            toast.error(`Delivered count (${delivered}) cannot exceed total numbers (${totalNumbers})`);
            return;
        }

        if (failed > totalNumbers) {
            toast.error(`Failed count (${failed}) cannot exceed total numbers (${totalNumbers})`);
            return;
        }

        if (pending > totalNumbers) {
            toast.error(`Pending count (${pending}) cannot exceed total numbers (${totalNumbers})`);
            return;
        }

        // Validate that all status counts sum to total numbers
        const statusSum = sent + delivered + failed + pending;

        if (statusSum > totalNumbers) {
            toast.error(`Total status counts (${statusSum}) exceed total numbers (${totalNumbers}). Please reduce the status counts or increase total numbers.`);
            return;
        }

        // Allow saving when statusSum < totalNumbers - the backend will handle auto-generation
        if (statusSum < totalNumbers) {
            const difference = totalNumbers - statusSum;
            console.log(`Total numbers (${totalNumbers}) > status sum (${statusSum}). ${difference} contacts will be auto-generated.`);
        }

        // Only validate listening statistics for Voice Call campaigns
        if (campaign.type === 'Voice Call') {
            const listeningTotal = formData.heard100Percent + formData.heard80to99Percent +
                formData.heard50to79Percent + formData.heard30to49Percent +
                formData.heard1to29Percent;

            // Allow for floating point precision issues by checking if total is close to 100
            if (listeningTotal > 0 && Math.abs(listeningTotal - 100) > 0.01) {
                toast.error(`Listening statistics must sum to 100%. Current total: ${listeningTotal}%`);
                return;
            }
        }

        setLoading(true);

        try {
            // Use enhanced update if total numbers changed from original or CSV file uploaded
            const originalTotalNumbers = campaign.statistics?.totalNumbers || 0;

            // Check if user explicitly changed total numbers (not just form initialization)
            const totalNumbersExplicitlyChanged = useEnhancedUpdate && (totalNumbers !== originalTotalNumbers);

            const shouldUseEnhanced = totalNumbersExplicitlyChanged || csvFile;

            if (shouldUseEnhanced) {
                // Initialize progress tracking for file uploads
                if (csvFile) {
                    setIsUploading(true);
                    setUploadProgress(0);
                    setUploadStage('uploading');
                    setUploadError(null);
                    setTotalRecords(0);
                    setProcessedRecords(0);
                    setValidRecords(0);
                    setInvalidRecords(0);
                    setDuplicateRecords(0);
                    setContactsUpdated(0);
                }

                // Show progress message for large operations
                if (creditsNeeded > 1000) {
                    toast.info('Processing large number of contacts... This may take a few minutes.');
                }

                // Create FormData for file upload
                const formDataToSend = new FormData();

                // Add all form fields
                Object.keys(formData).forEach(key => {
                    formDataToSend.append(key, formData[key]);
                });

                // Add CSV file if provided
                if (csvFile) {
                    formDataToSend.append('csvFile', csvFile);
                }

                let lastLoaded = 0;
                let lastTime = Date.now();

                const response = await campaignAPI.updateCampaignWithDetails(id, formDataToSend, {
                    onUploadProgress: csvFile ? (progressEvent) => {
                        const percentCompleted = Math.round(
                            (progressEvent.loaded * 100) / progressEvent.total
                        );
                        setUploadProgress(percentCompleted);

                        // Calculate upload speed
                        const currentTime = Date.now();
                        const timeDiff = currentTime - lastTime;
                        const loadedDiff = progressEvent.loaded - lastLoaded;

                        if (timeDiff > 0) {
                            const speed = (loadedDiff / timeDiff) * 1000; // bytes per second
                            setUploadSpeed(speed);
                        }

                        lastLoaded = progressEvent.loaded;
                        lastTime = currentTime;

                        // Update stage based on progress
                        if (percentCompleted < 100) {
                            setUploadStage('uploading');
                        } else {
                            setUploadStage('parsing');
                        }
                    } : undefined
                });

                // Real progress updates will come from the backend
                if (csvFile) {
                    setUploadStage('parsing');

                    // Update final progress states with real data from backend
                    const totalContacts = response.data.data?.totalContacts || 0;
                    setUploadStage('completed');
                    setProcessedRecords(totalContacts);
                    setValidRecords(totalContacts);
                    setRecordsWritten(totalContacts);
                    setRecordsPending(0);
                    setWriteProgress(100);
                }

                // Update user credits in context
                if (response.data.data.creditsDeducted > 0) {
                    await fetchUserCredits(); // Refresh credits
                }

                // Extract processing statistics from response
                if (csvFile && response.data.data) {
                    const data = response.data.data;
                    if (data.contactsAdded) setValidRecords(data.contactsAdded);
                    if (data.contactsUpdated) setContactsUpdated(data.contactsUpdated);
                    if (data.duplicatesSkipped) setDuplicateRecords(data.duplicatesSkipped);
                    if (data.invalidRecords) setInvalidRecords(data.invalidRecords);
                    if (data.processedRecords) setProcessedRecords(data.processedRecords);
                    setUploadStage('completed');
                    setUploadProgress(100);
                }

                setTimeout(() => {
                    // Show detailed success message
                    let successMessage = 'Campaign updated successfully';
                    if (response.data.data.contactsAdded > 0) {
                        successMessage += `. Added ${response.data.data.contactsAdded} new contacts`;
                    }
                    if (response.data.data.contactsUpdated > 0) {
                        successMessage += `. Updated ${response.data.data.contactsUpdated} existing contacts`;
                    }
                    if (response.data.data.totalNumbersUpdated) {
                        successMessage += `. Total numbers automatically increased`;
                    }
                    if (response.data.data.contactsRemoved > 0) {
                        successMessage += `. Removed ${response.data.data.contactsRemoved} contacts`;
                    }
                    if (response.data.data.duplicatesSkipped > 0) {
                        successMessage += `. Skipped ${response.data.data.duplicatesSkipped} duplicate contacts`;
                    }
                    if (response.data.data.creditsDeducted > 0) {
                        successMessage += `. ${response.data.data.creditsDeducted} credits deducted`;
                    }
                    if (response.data.data.creditsRefunded > 0) {
                        successMessage += `. ${response.data.data.creditsRefunded} credits refunded`;
                    }

                    toast.success(successMessage);
                    navigate('/campaigns');
                }, csvFile ? 2000 : 0);
            } else {
                // Use regular update (no file upload)
                await campaignAPI.updateCampaign(id, formData);
                toast.success('Campaign updated successfully');
                navigate('/campaigns');
            }
        } catch (error) {
            const errorMessage = error.response?.data?.message || 'Failed to update campaign';

            // Show more specific error messages for CSV validation failures
            if (errorMessage.includes('Missing required columns')) {
                const csvError = `CSV Validation Error: ${errorMessage}. \n\nRequired columns (case-insensitive): Phone Number, Timestamp, Status, Pulse (Seconds)`;
                if (csvFile) {
                    setUploadStage('error');
                    setUploadError(csvError);
                } else {
                    toast.error(csvError);
                }
            } else if (errorMessage.includes('Status field is blank') || errorMessage.includes('not exactly 10 digits')) {
                const csvError = `CSV Data Validation Error: ${errorMessage}`;
                if (csvFile) {
                    setUploadStage('error');
                    setUploadError(csvError);
                } else {
                    toast.error(csvError);
                }
            } else if (errorMessage.includes('Total count consistency error')) {
                toast.error(errorMessage);
            } else {
                if (csvFile) {
                    setUploadStage('error');
                    setUploadError(errorMessage);
                } else {
                    toast.error(errorMessage);
                }
            }
        } finally {
            // Don't reset loading immediately for better UX with progress modal
            setTimeout(() => {
                setLoading(false);
                setIsUploading(false);
                setUploadProgress(0);
                setUploadStage('uploading');
                setUploadSpeed(0);
                setProcessedRecords(0);
                setValidRecords(0);
                setInvalidRecords(0);
                setDuplicateRecords(0);
                setContactsUpdated(0);
                setRecordsWritten(0);
                setRecordsPending(0);
                setWriteProgress(0);
                setUploadError(null);
            }, csvFile ? 3000 : 0);
        }
    };

    const calculatePercentageTotal = () => {
        return formData.heard100Percent + formData.heard80to99Percent +
            formData.heard50to79Percent + formData.heard30to49Percent +
            formData.heard1to29Percent;
    };

    const getValidationWarnings = () => {
        const warnings = [];
        const totalNumbers = formData.totalNumbers;
        const sent = formData.sent;
        const delivered = formData.delivered;
        const failed = formData.failed;
        const pending = formData.pending;
        const statusSum = sent + delivered + failed + pending;

        // Check for negative values
        if (sent < 0 || delivered < 0 || failed < 0 || pending < 0) {
            warnings.push('Counts cannot be negative');
        }

        // Check individual counts against total
        if (sent > totalNumbers) {
            warnings.push(`Sent (${sent}) exceeds total numbers (${totalNumbers})`);
        }
        if (delivered > totalNumbers) {
            warnings.push(`Delivered (${delivered}) exceeds total numbers (${totalNumbers})`);
        }
        if (failed > totalNumbers) {
            warnings.push(`Failed (${failed}) exceeds total numbers (${totalNumbers})`);
        }
        if (pending > totalNumbers) {
            warnings.push(`Pending (${pending}) exceeds total numbers (${totalNumbers})`);
        }

        // Main validation: status sum should match total numbers
        if (statusSum !== totalNumbers) {
            if (statusSum > totalNumbers) {
                warnings.push(`Total status counts (${statusSum}) exceed total numbers (${totalNumbers}) - Please reduce status counts`);
            } else {
                const difference = totalNumbers - statusSum;
                warnings.push(`Total status counts (${statusSum}) are less than total numbers (${totalNumbers}). Missing ${difference} contacts will be auto-generated (90% delivered, 10% failed).`);
            }
        }

        return warnings;
    };

    if (loading && !campaign && !isUploading) {
        return (
            <div className="space-y-6">
                {/* Header skeleton */}
                <div className="flex items-center justify-between">
                    <div>
                        <div className="h-8 bg-soft-200 rounded-lg w-56 animate-pulse"></div>
                        <div className="h-4 bg-soft-100 rounded-lg w-72 mt-2 animate-pulse"></div>
                    </div>
                </div>

                {/* Main card skeleton */}
                <div className="card">
                    <div className="space-y-6">
                        {/* Loading header with animation */}
                        <div className="text-center py-8">
                            <div className="relative inline-flex items-center justify-center w-16 h-16 mb-4">
                                <div className="absolute inset-0 rounded-full border-4 border-primary-200 animate-spin border-t-primary-600"></div>
                                <div className="absolute inset-2 rounded-full border-2 border-primary-100 animate-spin border-t-primary-400" style={{ animationDirection: 'reverse', animationDuration: '1.5s' }}></div>
                                <PencilSquareIcon className="h-6 w-6 text-primary-600 animate-pulse" />
                            </div>
                            <h3 className="text-lg font-semibold text-soft-900 mb-2">Loading Campaign Editor</h3>
                            <p className="text-soft-600 mb-6">Preparing campaign data for editing...</p>

                            {/* Progress dots */}
                            <div className="flex items-center justify-center space-x-2">
                                <div className="w-2 h-2 bg-primary-400 rounded-full animate-bounce"></div>
                                <div className="w-2 h-2 bg-primary-400 rounded-full animate-bounce" style={{ animationDelay: '0.1s' }}></div>
                                <div className="w-2 h-2 bg-primary-400 rounded-full animate-bounce" style={{ animationDelay: '0.2s' }}></div>
                            </div>
                        </div>

                        {/* Campaign info skeleton */}
                        <div className="responsive-grid p-4 bg-soft-50 rounded-lg">
                            <div className="space-y-2">
                                <div className="h-4 bg-soft-200 rounded w-32 animate-pulse"></div>
                                <div className="h-6 bg-soft-100 rounded w-24 animate-pulse"></div>
                            </div>
                            <div className="space-y-2">
                                <div className="h-4 bg-soft-200 rounded w-28 animate-pulse"></div>
                                <div className="h-6 bg-soft-100 rounded w-36 animate-pulse"></div>
                            </div>
                        </div>

                        {/* Credits section skeleton */}
                        <div className="bg-gradient-to-r from-blue-50 to-indigo-50 p-4 rounded-lg border border-blue-200">
                            <div className="flex items-center justify-between mb-3">
                                <div className="flex items-center">
                                    <div className="h-5 w-5 bg-soft-200 rounded animate-pulse mr-2"></div>
                                    <div className="h-5 bg-soft-200 rounded w-48 animate-pulse"></div>
                                </div>
                                <div className="space-y-1">
                                    <div className="h-4 bg-soft-200 rounded w-32 animate-pulse"></div>
                                </div>
                            </div>
                            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                                {[...Array(3)].map((_, index) => (
                                    <div key={index} className="bg-white p-3 rounded-lg shadow-sm">
                                        <div className="h-4 bg-soft-200 rounded w-24 animate-pulse mb-2"></div>
                                        <div className="h-8 bg-soft-100 rounded w-16 animate-pulse mb-1"></div>
                                        <div className="h-3 bg-soft-200 rounded w-20 animate-pulse"></div>
                                    </div>
                                ))}
                            </div>
                        </div>

                        {/* Form skeleton */}
                        <div className="responsive-grid">
                            {[...Array(8)].map((_, index) => (
                                <div key={index} className="space-y-2">
                                    <div className="h-4 bg-soft-200 rounded w-24 animate-pulse"></div>
                                    <div className="h-10 bg-soft-100 rounded-lg animate-pulse"></div>
                                </div>
                            ))}
                        </div>

                        {/* File upload section skeleton */}
                        <div className="space-y-4">
                            <div className="h-4 bg-soft-200 rounded w-32 animate-pulse"></div>
                            <div className="h-32 bg-soft-100 rounded-lg border-2 border-dashed border-soft-300 animate-pulse"></div>
                        </div>

                        {/* Submit button skeleton */}
                        <div className="flex justify-end">
                            <div className="h-10 bg-soft-200 rounded-lg w-32 animate-pulse"></div>
                        </div>
                    </div>
                </div>

                {/* Loading message */}
                <div className="text-center">
                    <div className="inline-flex items-center space-x-2 text-soft-500 text-sm">
                        <div className="animate-spin rounded-full h-4 w-4 border-2 border-soft-300 border-t-primary-500"></div>
                        <span className="animate-pulse">Retrieving campaign configuration...</span>
                    </div>
                </div>
            </div>
        );
    }

    if (!campaign) {
        return null;
    }

    return (
        <div className="page-container">
            <div className="flex items-center justify-between">
                <div>
                    <h1 className="heading-responsive">Edit Campaign ID: {id}</h1>
                    <p className="subheading-responsive">Update campaign statistics and settings</p>
                </div>
            </div>

            <div className="card-responsive">
                <div className="form-section">
                    <div className="responsive-grid mb-6 p-4 bg-soft-50 rounded-lg">
                        <div>
                            <p className="text-sm font-medium text-soft-700">Campaign Type:</p>
                            <p className="text-lg font-semibold text-soft-900">{campaign.type}</p>
                        </div>
                        <div>
                            <p className="text-sm font-medium text-soft-700">Date Created:</p>
                            <p className="text-lg font-semibold text-soft-900">
                                {new Date(campaign.createdAt).toLocaleString()}
                            </p>
                        </div>
                    </div>

                    {/* Credit Information Section */}
                    {userCredits && campaign && (
                        <div className="bg-gradient-to-r from-blue-50 to-indigo-50 p-4 rounded-lg border border-blue-200">
                            <div className="flex items-center justify-between mb-3">
                                <div className="flex items-center">
                                    <CreditCardIcon className="h-5 w-5 text-blue-600 mr-2" />
                                    <h3 className="text-lg font-medium text-blue-900">Campaign Creator Credits</h3>
                                </div>
                                <div className="text-sm text-blue-700">
                                    <span className="font-medium">{userCredits.name}</span>
                                    <span className="text-blue-500 ml-1">({userCredits.email})</span>
                                </div>
                            </div>

                            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                                <div className="bg-white p-3 rounded-lg shadow-sm">
                                    <p className="text-sm font-medium text-gray-700">Available Credits</p>
                                    <p className="text-2xl font-bold text-blue-600">
                                        {getCampaignCredits().available.toLocaleString()}
                                    </p>
                                    <p className="text-xs text-gray-500">{getCampaignCredits().type} Credits</p>
                                </div>

                                <div className="bg-white p-3 rounded-lg shadow-sm">
                                    <p className="text-sm font-medium text-gray-700">Credit Rate</p>
                                    <p className="text-2xl font-bold text-green-600">
                                        {getCampaignCredits().rate}
                                    </p>
                                    <p className="text-xs text-gray-500">per phone number</p>
                                </div>

                                <div className="bg-white p-3 rounded-lg shadow-sm">
                                    <p className="text-sm font-medium text-gray-700">
                                        {calculateCreditsNeeded() > 0 ? 'Credits Needed' : calculateCreditsRefund() > 0 ? 'Credits to Refund' : 'Credits Impact'}
                                    </p>
                                    <p className={`text-2xl font-bold ${calculateCreditsNeeded() > 0
                                        ? (calculateCreditsNeeded() > getCampaignCredits().available ? 'text-red-600' : 'text-orange-600')
                                        : calculateCreditsRefund() > 0
                                            ? 'text-green-600'
                                            : 'text-gray-600'
                                        }`}>
                                        {calculateCreditsNeeded() > 0
                                            ? `-${calculateCreditsNeeded().toLocaleString()}`
                                            : calculateCreditsRefund() > 0
                                                ? `+${calculateCreditsRefund().toLocaleString()}`
                                                : '0'
                                        }
                                    </p>
                                    <p className="text-xs text-gray-500">
                                        {calculateCreditsNeeded() > 0
                                            ? 'will be deducted'
                                            : calculateCreditsRefund() > 0
                                                ? 'will be refunded'
                                                : 'no change'
                                        }
                                    </p>
                                </div>
                            </div>

                            {calculateCreditsNeeded() > getCampaignCredits().available && (
                                <div className="mt-3 p-3 bg-red-50 border border-red-200 rounded-lg">
                                    <div className="flex items-center">
                                        <ExclamationTriangleIcon className="h-5 w-5 text-red-600 mr-2" />
                                        <p className="text-sm text-red-700 font-medium">
                                            Insufficient Credits! You need {calculateCreditsNeeded() - getCampaignCredits().available} more credits.
                                        </p>
                                    </div>
                                </div>
                            )}
                        </div>
                    )}

                    <form onSubmit={handleSubmit} className="space-y-6">
                        <div className="responsive-grid">
                            <div>
                                <label className="block text-sm font-medium text-soft-700 mb-2">
                                    Total Numbers:
                                    <span className="text-xs text-soft-500 ml-1">(Max: {getMaxAllowedNumbers().toLocaleString()})</span>
                                </label>
                                <input
                                    type="number"
                                    name="totalNumbers"
                                    value={formData.totalNumbers}
                                    onChange={(e) => {
                                        handleInputChange(e);
                                        const originalTotal = campaign.statistics?.totalNumbers || 0;
                                        if (parseInt(e.target.value) !== originalTotal) {
                                            setUseEnhancedUpdate(true);
                                        }
                                    }}
                                    min="0"
                                    max={getMaxAllowedNumbers()}
                                    className="input-field"
                                    title="Changing this will automatically generate random Indian numbers (if increased) or remove contacts (if decreased)"
                                />
                                {formData.totalNumbers !== (campaign.statistics?.totalNumbers || 0) && (
                                    <div className="mt-1 space-y-1">
                                        <p className="text-xs text-blue-600">
                                            {formData.totalNumbers > (campaign.statistics?.totalNumbers || 0)
                                                ? `Will generate ${(formData.totalNumbers - (campaign.statistics?.totalNumbers || 0)).toLocaleString()} new Indian numbers`
                                                : `Will remove ${((campaign.statistics?.totalNumbers || 0) - formData.totalNumbers).toLocaleString()} contacts`
                                            }
                                        </p>
                                        {calculateCreditsNeeded() > 0 && (
                                            <p className={`text-xs ${calculateCreditsNeeded() > getCampaignCredits().available ? 'text-red-600' : 'text-green-600'}`}>
                                                Credits required: {calculateCreditsNeeded().toLocaleString()}
                                            </p>
                                        )}
                                        {calculateCreditsRefund() > 0 && (
                                            <p className="text-xs text-green-600">
                                                Credits to refund: {calculateCreditsRefund().toLocaleString()}
                                            </p>
                                        )}
                                    </div>
                                )}
                            </div>

                            <div>
                                <label className="block text-sm font-medium text-soft-700 mb-2">
                                    Count:
                                </label>
                                <input
                                    type="number"
                                    name="count"
                                    value={formData.count}
                                    onChange={handleInputChange}
                                    min="1"
                                    className="input-field"
                                />
                            </div>

                            <div>
                                <label className="block text-sm font-medium text-soft-700 mb-2">
                                    Sent:
                                </label>
                                <input
                                    type="number"
                                    name="sent"
                                    value={formData.sent}
                                    onChange={handleInputChange}
                                    min="0"
                                    className="input-field"
                                />
                            </div>

                            <div>
                                <label className="block text-sm font-medium text-soft-700 mb-2">
                                    Delivered:
                                </label>
                                <input
                                    type="number"
                                    name="delivered"
                                    value={formData.delivered}
                                    onChange={handleInputChange}
                                    min="0"
                                    className="input-field"
                                />
                            </div>

                            <div>
                                <label className="block text-sm font-medium text-soft-700 mb-2">
                                    Failed:
                                </label>
                                <input
                                    type="number"
                                    name="failed"
                                    value={formData.failed}
                                    onChange={handleInputChange}
                                    min="0"
                                    className="input-field"
                                />
                            </div>

                            <div>
                                <label className="block text-sm font-medium text-soft-700 mb-2">
                                    Pending:
                                </label>
                                <input
                                    type="number"
                                    name="pending"
                                    value={formData.pending}
                                    onChange={handleInputChange}
                                    min="0"
                                    className="input-field"
                                    title="Contacts not yet processed. Setting to 0 will assign random statuses to pending contacts."
                                />
                            </div>

                            <div>
                                <label className="block text-sm font-medium text-soft-700 mb-2">
                                    Pulse Duration (Seconds):
                                </label>
                                <input
                                    type="number"
                                    name="pulseDuration"
                                    value={formData.pulseDuration}
                                    onChange={handleInputChange}
                                    min="0"
                                    className="input-field"
                                />
                            </div>
                        </div>

                        {/* CSV Upload Section */}
                        <div className="border-t border-soft-200 pt-6">
                            <div className="flex items-center mb-4">
                                <DocumentArrowUpIcon className="h-5 w-5 text-soft-500 mr-2" />
                                <h3 className="text-lg font-medium text-soft-900">Update Phone Numbers via CSV</h3>
                            </div>

                            <div className="bg-blue-50 p-4 rounded-lg mb-4">
                                <div className="flex items-start">
                                    <InformationCircleIcon className="h-5 w-5 text-blue-500 mr-2 mt-0.5 flex-shrink-0" />
                                    <div className="text-sm text-blue-700">
                                        <p className="font-medium mb-2">📋 CSV Upload Requirements (All 4 columns are mandatory):</p>
                                        <div className="bg-white p-3 rounded border mb-3">
                                            <p className="font-semibold text-blue-800 mb-2">Required Columns (case-insensitive):</p>
                                            <ul className="list-disc list-inside space-y-1">
                                                <li><strong>Phone Number</strong> - Must be exactly 10 digits (6-9XXXXXXXX)</li>
                                                <li><strong>Timestamp</strong> - Date/time of contact attempt</li>
                                                <li><strong>Status</strong> - sent, delivered, failed, or pending (cannot be blank)</li>
                                                <li><strong>Pulse (Seconds)</strong> - Duration in seconds</li>
                                            </ul>
                                        </div>
                                        <p className="font-medium mb-2">📝 CSV Processing:</p>
                                        <ul className="list-disc list-inside space-y-1">
                                            <li><strong>Updates existing:</strong> If phone number exists, updates status/timestamp/pulse</li>
                                            <li><strong>Adds new contacts:</strong> New valid phone numbers are added to campaign</li>
                                            <li><strong>Auto-updates total:</strong> Total numbers automatically increases with new contacts from CSV</li>
                                            <li><strong>Strict validation:</strong> Rejects file if any row has blank status or invalid phone numbers</li>
                                            <li><strong>Recalculates statistics:</strong> All campaign stats are updated based on actual contact data</li>
                                            <li><strong>Record limit:</strong> Maximum 500,000 records per upload</li>
                                        </ul>
                                    </div>
                                </div>
                            </div>

                            <div>
                                <label className="block text-sm font-medium text-soft-700 mb-2">
                                    Upload CSV File (Optional):
                                </label>
                                <input
                                    type="file"
                                    accept=".csv"
                                    onChange={handleFileChange}
                                    className="block w-full text-sm text-soft-500 file:mr-4 file:py-2 file:px-4 file:rounded-full file:border-0 file:text-sm file:font-semibold file:bg-blue-50 file:text-blue-700 hover:file:bg-blue-100"
                                />
                                {csvFile && (
                                    <p className="text-sm text-green-600 mt-2">
                                        Selected file: {csvFile.name}
                                    </p>
                                )}
                            </div>
                        </div>

                        {/* Campaign Statistics Summary */}
                        <div className="border-t border-soft-200 pt-6">
                            <h3 className="text-lg font-medium text-soft-900 mb-4">Campaign Statistics Summary</h3>

                            <div className="responsive-grid mb-4">
                                <div className="bg-blue-50 p-4 rounded-lg">
                                    <h4 className="font-medium text-blue-900 mb-2">Current Totals</h4>
                                    <p className="text-xs text-blue-700 mb-3">
                                        Note: Pending contacts are normal for ongoing campaigns.
                                        Sent + Delivered + Failed + Pending = Total Numbers.
                                    </p>
                                    <div className="space-y-1 text-sm">
                                        <div className="flex justify-between">
                                            <span>Total Numbers:</span>
                                            <span className="font-medium">{formData.totalNumbers.toLocaleString()}</span>
                                        </div>
                                        <div className="flex justify-between">
                                            <span>Sent:</span>
                                            <span className="font-medium">{formData.sent.toLocaleString()}</span>
                                        </div>
                                        <div className="flex justify-between">
                                            <span>Delivered:</span>
                                            <span className="font-medium">{formData.delivered.toLocaleString()}</span>
                                        </div>
                                        <div className="flex justify-between">
                                            <span>Failed:</span>
                                            <span className="font-medium">{formData.failed.toLocaleString()}</span>
                                        </div>
                                        <div className="flex justify-between">
                                            <span>Pending:</span>
                                            <span className="font-medium text-yellow-600">{formData.pending.toLocaleString()}</span>
                                        </div>
                                        <div className="flex justify-between border-t pt-1 mt-2">
                                            <span>All Contacts (S+D+F+P):</span>
                                            <span className={`font-medium ${(formData.sent + formData.delivered + formData.failed + formData.pending) <= formData.totalNumbers ? 'text-blue-600' : 'text-red-600'}`}>
                                                {(formData.sent + formData.delivered + formData.failed + formData.pending).toLocaleString()}
                                            </span>
                                        </div>
                                        <div className="flex justify-between">
                                            <span>Completion Rate:</span>
                                            <span className="font-medium text-green-600">
                                                {(formData.sent + formData.delivered + formData.failed).toLocaleString()} / {formData.totalNumbers.toLocaleString()}
                                                ({formData.totalNumbers > 0 ? Math.round(((formData.sent + formData.delivered + formData.failed) / formData.totalNumbers) * 100) : 0}%)
                                            </span>
                                        </div>
                                        <div className="flex justify-between">
                                            <span>Total Progress:</span>
                                            <span className="font-medium text-blue-600">
                                                {(formData.sent + formData.delivered + formData.failed + formData.pending).toLocaleString()} / {formData.totalNumbers.toLocaleString()}
                                                ({formData.totalNumbers > 0 ? Math.round(((formData.sent + formData.delivered + formData.failed + formData.pending) / formData.totalNumbers) * 100) : 0}%)
                                            </span>
                                        </div>
                                    </div>
                                </div>

                                <div className="bg-green-50 p-4 rounded-lg">
                                    <h4 className="font-medium text-green-900 mb-2">Success Rates</h4>
                                    <div className="space-y-1 text-sm">
                                        <div className="flex justify-between">
                                            <span>Delivery Rate:</span>
                                            <span className="font-medium">
                                                {formData.sent > 0 ? ((formData.delivered / formData.sent) * 100).toFixed(1) : 0}%
                                            </span>
                                        </div>
                                        <div className="flex justify-between">
                                            <span>Failure Rate:</span>
                                            <span className="font-medium">
                                                {formData.sent > 0 ? ((formData.failed / formData.sent) * 100).toFixed(1) : 0}%
                                            </span>
                                        </div>
                                        <div className="flex justify-between">
                                            <span>Overall Progress:</span>
                                            <span className="font-medium">
                                                {formData.totalNumbers > 0 ? ((formData.sent / formData.totalNumbers) * 100).toFixed(1) : 0}%
                                            </span>
                                        </div>
                                    </div>
                                </div>
                            </div>

                            {/* Inconsistency Fix Button */}
                            {(() => {
                                const currentSum = formData.sent + formData.delivered + formData.failed + formData.pending;
                                const actualSum = campaign.overallStats ?
                                    (campaign.overallStats.sent + campaign.overallStats.delivered + campaign.overallStats.failed + campaign.overallStats.pending) : 0;

                                if (actualSum > 0 && formData.totalNumbers !== actualSum) {
                                    return (
                                        <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                                            <h4 className="font-medium text-yellow-900 mb-2">🔧 Data Inconsistency Detected</h4>
                                            <p className="text-sm text-yellow-700 mb-3">
                                                Total Numbers ({formData.totalNumbers}) doesn't match actual contacts ({actualSum}).
                                                Current form shows {currentSum} contacts in status fields.
                                            </p>
                                            <button
                                                type="button"
                                                onClick={() => {
                                                    setFormData(prev => ({
                                                        ...prev,
                                                        totalNumbers: actualSum
                                                    }));
                                                    toast.success(`Total numbers updated to match actual contacts: ${actualSum}`);
                                                }}
                                                className="btn-primary text-sm"
                                            >
                                                Fix Total Numbers ({actualSum})
                                            </button>
                                        </div>
                                    );
                                }
                                return null;
                            })()}

                            {/* Validation Warnings */}
                            {getValidationWarnings().length > 0 && (
                                <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                                    <h4 className="font-medium text-red-900 mb-2">⚠️ Validation Errors</h4>
                                    <ul className="space-y-1">
                                        {getValidationWarnings().map((warning, index) => (
                                            <li key={index} className="text-sm text-red-700">
                                                • {warning}
                                            </li>
                                        ))}
                                    </ul>
                                    <p className="text-xs text-red-600 mt-2">
                                        Please fix these issues before saving the campaign.
                                    </p>
                                </div>
                            )}

                            {/* Success Message */}
                            {getValidationWarnings().length === 0 && (
                                <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                                    <h4 className="font-medium text-green-900 mb-1">✅ Statistics Look Good</h4>
                                    <p className="text-sm text-green-700">
                                        All campaign statistics are within valid ranges.
                                        {formData.pending > 0 &&
                                            ` ${formData.pending.toLocaleString()} contacts are pending.`
                                        }
                                    </p>
                                </div>
                            )}
                        </div>

                        {/* Listening Statistics - Only show for Voice Call campaigns */}
                        {campaign.type === 'Voice Call' && (
                            <div className="border-t border-soft-200 pt-6">
                                <div className="flex items-center mb-4">
                                    <ChartBarIcon className="h-5 w-5 text-soft-500 mr-2" />
                                    <h3 className="text-lg font-medium text-soft-900">Listening Statistics</h3>
                                    <span className="ml-2 text-sm text-soft-500">
                                        (Total: {calculatePercentageTotal()}%)
                                    </span>
                                </div>

                                <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                                    <div>
                                        <label className="block text-sm font-medium text-soft-700 mb-2">
                                            Pulse 1-2 seconds (%):
                                        </label>
                                        <input
                                            type="number"
                                            name="heard100Percent"
                                            value={formData.heard100Percent}
                                            onChange={handleInputChange}
                                            min="0"
                                            max="100"
                                            step="0.1"
                                            className="input-field"
                                        />
                                    </div>

                                    <div>
                                        <label className="block text-sm font-medium text-soft-700 mb-2">
                                            Pulse 3-4 seconds (%):
                                        </label>
                                        <input
                                            type="number"
                                            name="heard80to99Percent"
                                            value={formData.heard80to99Percent}
                                            onChange={handleInputChange}
                                            min="0"
                                            max="100"
                                            step="0.1"
                                            className="input-field"
                                        />
                                    </div>

                                    <div>
                                        <label className="block text-sm font-medium text-soft-700 mb-2">
                                            Pulse 5-6 seconds (%):
                                        </label>
                                        <input
                                            type="number"
                                            name="heard50to79Percent"
                                            value={formData.heard50to79Percent}
                                            onChange={handleInputChange}
                                            min="0"
                                            max="100"
                                            step="0.1"
                                            className="input-field"
                                        />
                                    </div>

                                    <div>
                                        <label className="block text-sm font-medium text-soft-700 mb-2">
                                            Pulse 7-8 seconds (%):
                                        </label>
                                        <input
                                            type="number"
                                            name="heard30to49Percent"
                                            value={formData.heard30to49Percent}
                                            onChange={handleInputChange}
                                            min="0"
                                            max="100"
                                            step="0.1"
                                            className="input-field"
                                        />
                                    </div>

                                    <div className="lg:col-span-2">
                                        <label className="block text-sm font-medium text-soft-700 mb-2">
                                            Pulse 9-10 seconds (%):
                                        </label>
                                        <input
                                            type="number"
                                            name="heard1to29Percent"
                                            value={formData.heard1to29Percent}
                                            onChange={handleInputChange}
                                            min="0"
                                            max="100"
                                            step="0.1"
                                            className="input-field"
                                        />
                                    </div>
                                </div>

                                {calculatePercentageTotal() > 0 && calculatePercentageTotal() !== 100 && (
                                    <div className="mt-2 p-3 bg-red-50 border border-red-200 rounded-lg">
                                        <p className="text-sm text-red-700">
                                            Warning: Listening statistics should sum to 100%. Current total: {calculatePercentageTotal()}%
                                        </p>
                                    </div>
                                )}
                            </div>
                        )}

                        <div className="border-t border-soft-200 pt-6">
                            <label className="block text-sm font-medium text-soft-700 mb-2">
                                Status:
                            </label>
                            <select
                                name="status"
                                value={formData.status}
                                onChange={handleInputChange}
                                className="input-field max-w-xs"
                            >
                                <option value="Ongoing">Ongoing</option>
                                <option value="Paused">Paused</option>
                                <option value="Completed">Completed</option>
                            </select>
                        </div>

                        <div className="button-group-end">
                            <button
                                type="button"
                                onClick={() => navigate('/campaigns')}
                                className="btn-secondary flex items-center justify-center space-x-2 w-full sm:w-auto"
                            >
                                <XMarkIcon className="h-4 w-4" />
                                <span>Cancel</span>
                            </button>
                            <button
                                type="submit"
                                disabled={loading || getValidationWarnings().length > 0 || calculateCreditsNeeded() > getCampaignCredits().available}
                                className={`flex items-center justify-center space-x-2 w-full sm:w-auto ${getValidationWarnings().length > 0 || calculateCreditsNeeded() > getCampaignCredits().available ? 'btn-secondary opacity-50 cursor-not-allowed' : 'btn-primary'}`}
                            >
                                {loading ? (
                                    <>
                                        <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent"></div>
                                        <span className="hidden sm:inline">
                                            {calculateCreditsNeeded() > 1000 ? 'Processing Large Operation...' : 'Updating...'}
                                        </span>
                                        <span className="sm:hidden">
                                            {calculateCreditsNeeded() > 1000 ? 'Processing...' : 'Updating...'}
                                        </span>
                                    </>
                                ) : (
                                    <>
                                        <CheckIcon className="h-4 w-4" />
                                        <span className="hidden sm:inline">
                                            {getValidationWarnings().length > 0 ? 'Fix Validation Errors' :
                                                calculateCreditsNeeded() > getCampaignCredits().available ? 'Insufficient Credits' :
                                                    'Update Campaign'}
                                        </span>
                                        <span className="sm:hidden">
                                            {getValidationWarnings().length > 0 ? 'Fix Errors' :
                                                calculateCreditsNeeded() > getCampaignCredits().available ? 'No Credits' :
                                                    'Update'}
                                        </span>
                                    </>
                                )}
                            </button>
                        </div>
                    </form>

                    {/* Enhanced Upload Progress Modal */}
                    {isUploading && csvFile && (
                        <FileUploadProgress
                            progress={uploadProgress}
                            stage={uploadStage}
                            fileName={csvFile?.name || ''}
                            fileSize={csvFile?.size || 0}
                            uploadSpeed={uploadSpeed}
                            processedRecords={processedRecords}
                            totalRecords={totalRecords}
                            validRecords={validRecords}
                            invalidRecords={invalidRecords}
                            duplicateRecords={duplicateRecords}
                            contactsUpdated={contactsUpdated}
                            recordsWritten={recordsWritten}
                            recordsPending={recordsPending}
                            writeProgress={writeProgress}
                            error={uploadError}
                            onCancel={() => {
                                setIsUploading(false);
                                setUploadProgress(0);
                                setUploadStage('uploading');
                                setUploadSpeed(0);
                                setProcessedRecords(0);
                                setValidRecords(0);
                                setInvalidRecords(0);
                                setDuplicateRecords(0);
                                setContactsUpdated(0);
                                setRecordsWritten(0);
                                setRecordsPending(0);
                                setWriteProgress(0);
                                setUploadError(null);
                            }}
                        />
                    )}
                </div>
            </div>
        </div>
    );
};

export default EditCampaign; 